/**
 * Автомат состояний тура (E4.6b): idle → preparing → showing → paused/finished. Шаг
 * показывается только когда его цель найдена на странице; недоступная цель даёт повтор
 * или пропуск, и пропуск просмотром не считается. Страница шага, закрытая для роли пользователя
 * (охрана маршрута увела с адреса шага), -- тоже недоступный шаг, со своей причиной. Переход на другой адрес при
 * незавершённом черновике удерживает тур (blocked) до явного решения пользователя:
 * «Продолжить» -- возврат в фазу до удержания (недоступный шаг остаётся недоступным, шаг в
 * подготовке снова ждёт цель, и ни тот ни другой не засчитывается), сохранение или отмена
 * черновика -- переход. Шаг в подготовке можно пропустить.
 */

import { LESSONS, type LessonId } from './catalog';

export type TourPhase =
	| 'idle'
	| 'welcome'
	| 'preparing'
	| 'showing'
	| 'unavailable'
	| 'blocked'
	| 'finished'
	| 'paused';

/** Почему шаг недоступен: цели нет на странице или сама страница шага закрыта для роли */
export type UnavailableReason = 'target' | 'page';

/** Фазы, из которых переход может ждать решения о черновике */
const HOLDABLE_PHASES = ['preparing', 'showing', 'unavailable', 'finished'] as const;
type HoldablePhase = (typeof HOLDABLE_PHASES)[number];

/** Фаза до удержания черновиком: в неё возвращает «Продолжить» */
export interface HeldPhase {
	phase: HoldablePhase;
	unavailable: UnavailableReason | null;
}

export interface TourState {
	phase: TourPhase;
	lesson: LessonId | null;
	index: number;
	/** Шаг, переход к которому ждёт решения о черновике */
	pending: number | null;
	/** Причина недоступности; только в фазе unavailable */
	unavailable: UnavailableReason | null;
	/** Фаза до удержания; только в фазе blocked */
	held: HeldPhase | null;
}

export type TourEvent =
	| { type: 'start'; lesson: LessonId; index: number; welcome?: boolean }
	| { type: 'begin' }
	| { type: 'ready' }
	| { type: 'missing' }
	/* Охрана маршрута увела с адреса шага: страница шага закрыта для роли */
	| { type: 'closed' }
	| { type: 'retry' }
	/* dirty: переход сменит адрес, а в реестре есть незавершённый черновик */
	| { type: 'next'; dirty: boolean }
	/* Пропуск шага, цель которого ещё готовится: дальше без просмотра */
	| { type: 'skip'; dirty: boolean }
	| { type: 'back'; dirty: boolean }
	| { type: 'jump'; index: number; dirty: boolean }
	/* «Пройти с начала»: и с экрана шага, и после финала урока */
	| { type: 'restart'; dirty: boolean }
	| { type: 'continue' }
	| { type: 'draftsResolved' }
	| { type: 'pause' }
	| { type: 'resume' }
	| { type: 'close' };

export const IDLE_TOUR: TourState = {
	phase: 'idle',
	lesson: null,
	index: 0,
	pending: null,
	unavailable: null,
	held: null,
};

function stepCount(state: TourState): number {
	return state.lesson === null ? 0 : LESSONS[state.lesson].steps.length;
}

/** Переход к шагу: сразу или после решения о черновике */
function go(state: TourState, index: number, dirty: boolean): TourState {
	if (index < 0 || index >= stepCount(state)) return state;
	if (!dirty) return { ...state, phase: 'preparing', index, pending: null };
	const held: HeldPhase | null = isHoldable(state.phase)
		? { phase: state.phase, unavailable: state.unavailable }
		: null;
	return { ...state, phase: 'blocked', pending: index, held };
}

function isHoldable(phase: TourPhase): phase is HoldablePhase {
	return (HOLDABLE_PHASES as readonly TourPhase[]).includes(phase);
}

/** Следующий шаг или финал урока */
function advance(state: TourState, dirty: boolean): TourState {
	return state.index + 1 < stepCount(state)
		? go(state, state.index + 1, dirty)
		: { ...state, phase: 'finished' };
}

/** Шаг на экране: можно листать дальше и назад */
function onStep(state: TourState): boolean {
	return state.phase === 'showing' || state.phase === 'unavailable';
}

/** Причина недоступности живёт только в фазе unavailable, удержанная фаза -- только в blocked */
export function tourReducer(state: TourState, event: TourEvent): TourState {
	let next = transition(state, event);
	if (next.phase !== 'unavailable' && next.unavailable !== null)
		next = { ...next, unavailable: null };
	if (next.phase !== 'blocked' && next.held !== null) next = { ...next, held: null };
	return next;
}

function transition(state: TourState, event: TourEvent): TourState {
	switch (event.type) {
		case 'start': {
			const lesson = LESSONS[event.lesson];
			if (!lesson || event.index < 0 || event.index >= lesson.steps.length) return state;
			return {
				phase: event.welcome ? 'welcome' : 'preparing',
				lesson: event.lesson,
				index: event.index,
				pending: null,
				unavailable: null,
				held: null,
			};
		}
		case 'begin':
			return state.phase === 'welcome' ? { ...state, phase: 'preparing', index: 0 } : state;
		case 'ready':
			return state.phase === 'preparing' ? { ...state, phase: 'showing' } : state;
		case 'missing':
			return state.phase === 'preparing'
				? { ...state, phase: 'unavailable', unavailable: 'target' }
				: state;
		case 'closed':
			return state.phase === 'preparing' || onStep(state)
				? { ...state, phase: 'unavailable', unavailable: 'page' }
				: state;
		case 'retry':
			return state.phase === 'unavailable' ? { ...state, phase: 'preparing' } : state;
		case 'next':
			return onStep(state) ? advance(state, event.dirty) : state;
		case 'skip':
			return state.phase === 'preparing' ? advance(state, event.dirty) : state;
		case 'back':
			return onStep(state) || state.phase === 'preparing'
				? go(state, state.index - 1, event.dirty)
				: state;
		case 'jump':
			return onStep(state) && state.lesson === 'demo' ? go(state, event.index, event.dirty) : state;
		case 'restart':
			return onStep(state) || state.phase === 'finished' ? go(state, 0, event.dirty) : state;
		/* Без удержанной фазы -- подготовка: шаг засчитывается только после нового показа цели */
		case 'continue':
			return state.phase === 'blocked'
				? {
						...state,
						phase: state.held?.phase ?? 'preparing',
						unavailable: state.held?.unavailable ?? null,
						pending: null,
					}
				: state;
		case 'draftsResolved':
			return state.phase === 'blocked' && state.pending !== null
				? { ...state, phase: 'preparing', index: state.pending, pending: null }
				: state;
		case 'pause':
			return state.phase === 'idle' ? state : { ...state, phase: 'paused', pending: null };
		case 'resume':
			return state.phase === 'paused' ? { ...state, phase: 'preparing' } : state;
		case 'close':
			return IDLE_TOUR;
	}
}
